import { useEffect, useState } from 'react';
import { Box, Title, Button, TextInput, Select, Textarea, Group, Stack, Text } from '@mantine/core';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import { createLead, fetchCampaigns } from '../services/api';

const SOURCE_OPTIONS = ['instagram-dm', 'linkedin', 'referral', 'cold-email', 'whatsapp', 'sms', 'other'];

export default function NewLeadPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const [campaignId, setCampaignId] = useState(searchParams.get('campaignId') || null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    async function loadCampaigns() {
      try {
        const data = await fetchCampaigns(api);
        const active = (data.campaigns || []).filter(
          (c) => c.status === 'active' || c.status === 'draft'
        );
        setCampaigns(active);
      } catch (err) {
        notifications.show({
          title: 'Failed to load campaigns',
          message: err.response?.data?.error || 'Something went wrong',
          color: 'red',
        });
      }
    }
    loadCampaigns();
  }, [api]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        company: company.trim(),
        email: email.trim(),
        phone: phone.trim(),
        title: title.trim(),
        source: source || '',
        stage: 'prospect',
        campaignId: campaignId || null,
        notes: notes.trim(),
      };
      const newLead = await createLead(api, payload);
      notifications.show({
        title: 'Lead created',
        message: 'Lead has been added successfully.',
        color: 'teal',
      });
      navigate(`/leads/${newLead.id}`);
    } catch (err) {
      notifications.show({
        title: 'Failed to create lead',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box p="md" maw={760}>
      <Group mb="md">
        <Button variant="subtle" component={Link} to="/pipeline">← Back to Pipeline</Button>
      </Group>

      <Title order={2} mb={4}>Add New Lead</Title>
      <Text c="dimmed" size="sm" mb="md">Create a new lead record for your pipeline.</Text>

      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
          <TextInput label="Company" value={company} onChange={(e) => setCompany(e.currentTarget.value)} />
          <TextInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          <TextInput label="Phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
          <TextInput label="Title" value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
          <Select
            label="Source"
            data={SOURCE_OPTIONS}
            value={source}
            onChange={(value) => setSource(value || '')}
          />
          <Select
            label="Campaign"
            data={campaigns.map((c) => ({ value: c.id, label: c.name }))}
            value={campaignId}
            onChange={(value) => setCampaignId(value)}
            clearable
            placeholder="No campaign"
          />
          <Textarea label="Notes" rows={3} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />

          <Group justify="flex-end" mt="sm">
            <Button type="submit" loading={submitting}>Create Lead</Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
}
